import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { ShoppingCart, ArrowLeft, Check, Search, Trash2, ScanLine, Plus, Store } from 'lucide-react';
import nurseryService from '../../services/nursery';
import nurseryAccountsService from '../../services/nurseryAccounts';
import {
  CultivarInfoDialog, CustomerOpenOrders, CustomerPicker, OrderDespatchFields, OrderScanDialog, OrderSheetGrid, type CellState, type ScanOutcome, type ScanResult,
} from '../../components/nursery';
import { usePointerCoarse } from '../../components/map/usePointerCoarse';
import { getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import { errorMessage, unwrap } from '../../utils/apiResponse';
import { money, num } from '../../utils/nurseryMoney';
import {
  filterSheet, findRowByBarcode, groupSheet, isProductMember, parseQuantity, rowSubject, sheetCounts, sheetCrops, sheetTreeTypes,
  type SheetShow,
} from '../../utils/orderSheet';
import type { CreditPosition, CustomerAccount } from '../../types/nurseryAccounts';
import type {
  NurseryCustomer, CreateOrderPayload, NurseryCustomerAddress, NurseryDeliveryZone,
  NurseryOrder, NurseryOrderStatus, OrderDespatchPayload, OrderSheetOrder, OrderSheetPricing, OrderSheetRow,
} from '../../types/nursery';

/** "Products only" is remembered per browser: whoever enters orders wants it the same each time. */
const PRODUCTS_ONLY_KEY = 'nursery_order_sheet_products_only';

/**
 * Entering an order: the order sheet.
 *
 * Everything the nursery sells is on the page with its free stock and a
 * quantity box, so an order is typed down the page rather than built one
 * dropdown at a time. Nothing is priced by hand here — the server prices each
 * line from the customer's price list.
 *
 * IT SAVES AS YOU GO. The first quantity creates the order as a draft; every
 * box after that saves when it is left, and the header saves when it changes.
 * A draft is on no load and in no planner, so a half-entered order costs
 * nothing, and a closed tab loses nothing: the page reopens on `?order=<id>`.
 *
 * Saves run one at a time, in the order they were made. The order has to
 * exist before its first line can be written, and two lines saved at once
 * would race for the order's totals.
 *
 * PRICES ARE HIDDEN UNTIL ASKED FOR. "Show prices" puts the chosen
 * customer's price beside every row; it starts off on every order, because
 * the screen is often turned to the customer across the counter.
 *
 * MORE THAN IS FREE CAN BE ORDERED. The rest waits in line for stock that
 * becomes ready, first come, first served (the server allocates it to the
 * waiting orders in turn); each row says where the order stands.
 *
 * TREES CAN BE SCANNED ON instead of typed, for a sale across the counter:
 * each scan adds one to the line carrying that barcode and saves it exactly
 * as a typed quantity is saved.
 */

/** The camera reads a code on every frame it is in view: it must leave view this long to count again. */
const CAMERA_REPEAT_MS = 1500;

type Header = {
  customerId: number | null;
  customerPo: string;
  orderDate: string;
  requested: string;
  estimated: string;
  notes: string;
  despatch: OrderDespatchPayload;
};

/** The header as the order stores it, for telling what has changed since the last save. */
function headerFields(h: Header): Record<string, unknown> {
  const fields: Record<string, unknown> = {
    customer_po: h.customerPo.trim() || null,
    requested_delivery_date: h.requested || null,
    estimated_delivery_date: h.estimated || null,
    notes: h.notes.trim() || null,
    despatch_method: h.despatch.despatch_method,
    despatch_from: h.despatch.despatch_from ?? null,
    despatch_by: h.despatch.despatch_by ?? null,
    priority: h.despatch.priority,
    collection_at: h.despatch.collection_at ?? null,
    delivery_address_id: h.despatch.delivery_address_id ?? null,
  };
  // Left out while "work it out from the address": the server keeps the zone
  // unless the address or method moves.
  if (h.despatch.delivery_zone_id !== undefined) fields.delivery_zone_id = h.despatch.delivery_zone_id;
  return fields;
}

// The statuses the sheet can show by name are in the locale file, under statusText.<status>.

/** Quantities can change until picking starts. The server's `editable` is the answer; this is for before it arrives. */
const SHEET_EDITABLE: NurseryOrderStatus[] = ['draft', 'confirmed', 'partially_allocated', 'allocated'];

const inputCls =
  'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/** The order just confirmed with "Confirm & new order", named on the next sheet. */
interface Previous { id: number; order_number: string }

function OrderSheet({ previous, onStartAnother, onOpenOrder }: {
  previous: Previous | null;
  onStartAnother: (done: Previous) => void;
  /** Carry on entering an order the customer already has. */
  onOpenOrder: (orderId: number) => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'OrderCreate' });
  const navigate = useNavigate();
  const timezone = useOrgTimezone();
  const [searchParams, setSearchParams] = useSearchParams();
  // The order this page was opened on, if any. Read once: the page sets the
  // parameter itself when it creates the draft, and must not reload for that.
  const openedOn = useRef(Number(searchParams.get('order')) || null).current;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<NurseryCustomer[]>([]);
  const [zones, setZones] = useState<NurseryDeliveryZone[]>([]);
  const [addresses, setAddresses] = useState<NurseryCustomerAddress[]>([]);
  const [account, setAccount] = useState<CustomerAccount | null>(null);
  const [credit, setCredit] = useState<CreditPosition | null>(null);

  // Header
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customerPo, setCustomerPo] = useState('');
  const [orderDate, setOrderDate] = useState(() => getTodayInTz(timezone));
  const [requested, setRequested] = useState('');
  const [estimated, setEstimated] = useState('');
  const [notes, setNotes] = useState('');
  const [despatch, setDespatch] = useState<OrderDespatchPayload>({ despatch_method: 'run', priority: 'normal' });
  const [currentZoneName, setCurrentZoneName] = useState<string | null>(null);
  const [poError, setPoError] = useState<string | null>(null);
  const [headerError, setHeaderError] = useState<string | null>(null);

  // The sheet
  const [rows, setRows] = useState<OrderSheetRow[]>([]);
  const [farms, setFarms] = useState<{ id: number; name: string }[]>([]);
  const [farmId, setFarmId] = useState<number | null>(null);
  const [show, setShow] = useState<SheetShow>('all');
  const [search, setSearch] = useState('');
  const [treeTypeId, setTreeTypeId] = useState<number | null>(null);
  const [crop, setCrop] = useState<string | null>(null);
  const [productsOnly, setProductsOnly] = useState<boolean>(() => {
    try { return localStorage.getItem(PRODUCTS_ONLY_KEY) === '1'; } catch { return false; }
  });
  const [sheetError, setSheetError] = useState<string | null>(null);
  // Off on every order: the sheet is often turned to the customer.
  const [showPrices, setShowPrices] = useState(false);
  const [pricing, setPricing] = useState<OrderSheetPricing | null>(null);

  // The order being entered, once it exists.
  const [order, setOrder] = useState<OrderSheetOrder | null>(null);
  const orderIdRef = useRef<number | null>(openedOn);

  // Cells: what each box holds, what the server holds, and how the save went.
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const quantitiesRef = useRef<Record<string, string>>({});
  const [saved, setSaved] = useState<Record<string, number>>({});
  const savedRef = useRef<Record<string, number>>({});
  const [lineTotals, setLineTotals] = useState<Record<string, number>>({});
  // Saved lines no price list priced: they invoice at nothing until priced.
  const [unpriced, setUnpriced] = useState<Record<string, boolean>>({});
  const [cells, setCells] = useState<Record<string, CellState>>({});

  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Lowering a line below what it had allocated sent trees back to free stock.
  const [releaseNote, setReleaseNote] = useState<string | null>(null);
  // The row whose description is open: what to tell the customer about it.
  const [infoRow, setInfoRow] = useState<OrderSheetRow | null>(null);
  const closeInfo = useCallback(() => setInfoRow(null), []);

  // Scanning trees on.
  const coarsePointer = usePointerCoarse();
  const [scanning, setScanning] = useState(false);
  const [scans, setScans] = useState<ScanResult[]>([]);
  const scanSeq = useRef(0);
  const cameraSeen = useRef<{ code: string; at: number } | null>(null);

  const header: Header = useMemo(
    () => ({ customerId, customerPo, orderDate, requested, estimated, notes, despatch }),
    [customerId, customerPo, orderDate, requested, estimated, notes, despatch],
  );
  // What a queued save reads when its turn comes, which may be after a re-render.
  const headerRef = useRef(header);
  useEffect(() => { headerRef.current = header; }, [header]);
  // The header as last saved, so only what changed is sent.
  const sentHeaderRef = useRef<Record<string, unknown>>({});

  const poRequired = !!account?.po_required;
  const poRequiredRef = useRef(poRequired);
  useEffect(() => { poRequiredRef.current = poRequired; }, [poRequired]);
  const poRequiredMessage = account ? t('poRequired', { payer: account.payer_name }) : '';

  // ── The save queue ──
  const chain = useRef<Promise<void>>(Promise.resolve());
  const waiting = useRef(0);
  const enqueue = useCallback((job: () => Promise<void>): Promise<void> => {
    waiting.current += 1;
    setBusy(true);
    const run = chain.current.then(job).catch(() => { /* each job reports its own failure */ }).finally(() => {
      waiting.current -= 1;
      if (waiting.current === 0) setBusy(false);
    });
    chain.current = run;
    return run;
  }, []);

  const setCell = useCallback((key: string, state: CellState | null) => {
    setCells((prev) => {
      if (!state && !prev[key]) return prev;
      const next = { ...prev };
      if (state) next[key] = state;
      else delete next[key];
      return next;
    });
  }, []);

  /** Create the draft the first time something needs an order to exist. */
  const ensureOrder = useCallback(async (): Promise<number> => {
    if (orderIdRef.current) return orderIdRef.current;
    const h = headerRef.current;
    if (!h.customerId) throw new Error(t('chooseCustomerFirst'));
    const fields = headerFields(h);
    const payload = {
      customer_id: h.customerId,
      order_date: h.orderDate,
      placed_by_org: 'nursery',
      ...Object.fromEntries(Object.entries(fields).filter(([, v]) => v != null)),
    } as CreateOrderPayload;
    const created = unwrap(await nurseryService.createOrder(payload), t('createFailed'));
    orderIdRef.current = created.id;
    sentHeaderRef.current = fields;
    setOrder({
      id: created.id, order_number: created.order_number, status: created.status ?? 'draft',
      total_trees: 0, total_value: 0, editable: true,
    });
    setSearchParams({ order: String(created.id) }, { replace: true });
    return created.id;
  }, [setSearchParams, t]);

  // ── Loading ──

  const applyOrderHeader = useCallback((o: NurseryOrder) => {
    const next: Header = {
      customerId: o.customer_id,
      customerPo: o.customer_po ?? '',
      orderDate: (o.order_date || '').slice(0, 10),
      requested: (o.requested_delivery_date || '').slice(0, 10),
      estimated: (o.estimated_delivery_date || '').slice(0, 10),
      notes: o.notes ?? '',
      despatch: {
        despatch_method: o.despatch_method ?? 'collection',
        despatch_from: o.despatch_from ? o.despatch_from.slice(0, 10) : null,
        despatch_by: o.despatch_by ? o.despatch_by.slice(0, 10) : null,
        priority: o.priority ?? 'normal',
        collection_at: o.collection_at ?? null,
        delivery_address_id: o.delivery_address_id ?? null,
      },
    };
    sentHeaderRef.current = headerFields(next);
    headerRef.current = next;
    setCustomerId(next.customerId);
    setCustomerPo(next.customerPo);
    setOrderDate(next.orderDate);
    setRequested(next.requested);
    setEstimated(next.estimated);
    setNotes(next.notes);
    setDespatch(next.despatch);
    setCurrentZoneName(o.delivery_zone_name ?? null);
    // Until the sheet answers; its totals are the ones kept.
    setOrder((prev) => prev ?? {
      id: o.id, order_number: o.order_number, status: o.status,
      total_trees: o.total_trees, total_value: num(o.total_value),
      editable: SHEET_EDITABLE.includes(o.status),
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [custRes, zoneRes, orderRes] = await Promise.all([
          nurseryService.getCustomers({ active: true }),
          nurseryService.getDeliveryZones(),
          openedOn ? nurseryService.getOrder(openedOn) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (custRes.success) setCustomers(custRes.data || []);
        if (zoneRes.success) setZones(zoneRes.data || []);
        if (openedOn) {
          if (orderRes?.success && orderRes.data) applyOrderHeader(orderRes.data);
          else setLoadError(t('orderNotFound'));
        }
      } catch (e) {
        if (!cancelled) setLoadError(errorMessage(e, t('formFailed')));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // `t` only words an error: a language change must not reload the header over edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedOn, applyOrderHeader]);

  // The sheet: reloaded when the farm changes, for its stock figures, and when
  // the customer is chosen, for their prices. The quantities on screen are the
  // page's own after the first load.
  const sheetLoaded = useRef(false);
  useEffect(() => {
    let cancelled = false;
    nurseryService.getOrderSheet({ farmId, orderId: orderIdRef.current, customerId }).then((res) => {
      if (cancelled) return;
      const sheet = unwrap(res, t('stockFailed'));
      setRows(sheet?.rows || []);
      setFarms(sheet?.farms || []);
      setPricing(sheet?.pricing ?? null);
      setSheetError(null);
      if (!sheetLoaded.current) {
        sheetLoaded.current = true;
        const text: Record<string, string> = {};
        const held: Record<string, number> = {};
        const totals: Record<string, number> = {};
        const noPrice: Record<string, boolean> = {};
        for (const r of sheet?.rows || []) {
          if (r.quantity > 0) {
            text[r.key] = String(r.quantity);
            held[r.key] = r.quantity;
            totals[r.key] = num(r.line_total);
            if (r.unit_price == null) noPrice[r.key] = true;
          }
        }
        quantitiesRef.current = text;
        savedRef.current = held;
        setQuantities(text);
        setSaved(held);
        setLineTotals(totals);
        setUnpriced(noPrice);
        if (sheet?.order) setOrder(sheet.order);
      }
    }).catch((e) => {
      if (!cancelled) setSheetError(errorMessage(e, t('stockFailed')));
    });
    return () => { cancelled = true; };
    // `t` only words an error: a language change must not refetch the sheet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmId, customerId]);

  // The chosen customer's addresses, who pays, and where that account stands.
  useEffect(() => {
    let cancelled = false;
    setAddresses([]);
    setAccount(null);
    setCredit(null);
    if (!customerId) return;
    // A customer chosen for a new order starts from their default address; an
    // order being reopened keeps the address it was saved with.
    const fresh = !orderIdRef.current;
    if (fresh) setDespatch((d) => ({ ...d, delivery_address_id: null }));
    // The account position is a warning beside the form; without it the order is placed as before.
    nurseryAccountsService.getAccount(customerId).then((res) => {
      if (!cancelled && res.success) setCredit(res.data?.credit ?? null);
    }).catch(() => { /* no warning shown */ });
    nurseryService.getCustomer(customerId).then((res) => {
      if (cancelled || !res.success || !res.data) return;
      setAccount(res.data.account ?? null);
      const list = res.data.addresses || [];
      setAddresses(list);
      const def = list.find((a) => a.address_type === 'delivery' && a.is_default);
      if (fresh && def?.id) setDespatch((d) => ({ ...d, delivery_address_id: def.id }));
    }).catch(() => { /* the fields say the customer has no address */ });
    return () => { cancelled = true; };
  }, [customerId]);

  // ── Saving ──

  const onType = useCallback((key: string, text: string) => {
    quantitiesRef.current = { ...quantitiesRef.current, [key]: text };
    setQuantities(quantitiesRef.current);
  }, []);

  const onLeave = useCallback((row: OrderSheetRow) => {
    const quantity = parseQuantity(quantitiesRef.current[row.key] ?? '');
    if (quantity == null) {
      setCell(row.key, { state: 'error', message: t('wholeNumbers') });
      return;
    }
    if (quantity === (savedRef.current[row.key] ?? 0)) {
      setCell(row.key, null);
      return;
    }
    if (!orderIdRef.current && poRequiredRef.current && !headerRef.current.customerPo.trim()) {
      setPoError(t('enterPoFirst'));
      setCell(row.key, { state: 'error', message: t('notSavedNeedsPo') });
      return;
    }
    setCell(row.key, { state: 'saving' });
    enqueue(async () => {
      try {
        const orderId = await ensureOrder();
        const result = unwrap(await nurseryService.setOrderSheetLine(orderId, rowSubject(row), quantity), t('notSaved'));
        savedRef.current = { ...savedRef.current, [row.key]: quantity };
        setSaved(savedRef.current);
        setLineTotals((prev) => ({ ...prev, [row.key]: result.line ? num(result.line.line_total) : 0 }));
        setUnpriced((prev) => ({ ...prev, [row.key]: !!result.line && result.line.unit_price == null }));
        setOrder((o) => (o ? {
          ...o, total_trees: result.order.total_trees, total_value: num(result.order.total_value),
          status: result.order.status ?? o.status,
        } : o));
        const allocatedNow = result.line?.allocated ?? 0;
        setRows((prev) => prev.map((r) => (r.key === row.key && (r.allocated ?? 0) !== allocatedNow
          ? { ...r, allocated: allocatedNow, free: r.free + (result.released ?? 0) } : r)));
        if (result.released) {
          setReleaseNote(t('released', { count: result.released, name: row.name }));
        }
        // Typed again while this was saving: that value is for the next save to settle.
        if (parseQuantity(quantitiesRef.current[row.key] ?? '') === quantity) setCell(row.key, null);
      } catch (e) {
        setCell(row.key, { state: 'error', message: t('notSavedReason', { reason: errorMessage(e, t('tryAgain')) }) });
      }
    });
  }, [enqueue, ensureOrder, setCell, t]);

  /** Add (or with -1 take off) one tree of a row, saved as a typed quantity is. */
  const scanResult = useCallback((result: ScanOutcome) => {
    scanSeq.current += 1;
    const next: ScanResult = { ...result, id: scanSeq.current };
    setScans((prev) => [next, ...prev].slice(0, 6));
  }, []);

  const adjustByOne = useCallback((row: OrderSheetRow, delta: 1 | -1, code: string) => {
    if (row.line_count > 1) {
      scanResult({ kind: 'refused', code, message: t('severalLines', { name: row.name }) });
      return;
    }
    if (!orderIdRef.current && poRequiredRef.current && !headerRef.current.customerPo.trim()) {
      setPoError(t('enterPoFirst'));
      scanResult({ kind: 'refused', code, message: t('nothingAddedNeedsPo') });
      return;
    }
    const typed = parseQuantity(quantitiesRef.current[row.key] ?? '');
    const now = typed ?? savedRef.current[row.key] ?? 0;
    const quantity = Math.max(0, now + delta);
    onType(row.key, quantity ? String(quantity) : '');
    onLeave(row);
    scanResult({ kind: 'added', row, quantity });
  }, [onType, onLeave, scanResult, t]);

  const onScan = useCallback((scanned: string, source: 'camera' | 'manual') => {
    const code = scanned.trim();
    if (!code) return;
    if (source === 'camera') {
      const seen = cameraSeen.current;
      const at = Date.now();
      cameraSeen.current = { code, at };
      if (seen && seen.code === code && at - seen.at < CAMERA_REPEAT_MS) return;
    }
    const row = findRowByBarcode(rows, code);
    if (!row) {
      scanResult({ kind: 'unknown', code });
      return;
    }
    adjustByOne(row, 1, code);
  }, [rows, adjustByOne, scanResult]);

  const takeOneOff = useCallback((row: OrderSheetRow) => adjustByOne(row, -1, ''), [adjustByOne]);
  const closeScanner = useCallback(() => setScanning(false), []);

  // A sale across the counter leaves with the customer: a new order still on
  // the default method becomes a collection, collected now. One already
  // started, or a method somebody chose, is left as it is.
  const makeCounterSale = useCallback(() => {
    if (!orderIdRef.current && headerRef.current.despatch.despatch_method === 'run') {
      setDespatch((d) => ({ ...d, despatch_method: 'collection', collection_at: d.collection_at ?? new Date().toISOString() }));
    }
  }, []);

  const startScanning = useCallback(() => {
    makeCounterSale();
    setScanning(true);
  }, [makeCounterSale]);

  // The walk-in customer, made the first time anybody asks for it.
  const [walkInBusy, setWalkInBusy] = useState(false);
  const [walkInError, setWalkInError] = useState<string | null>(null);
  const chooseWalkIn = useCallback(async () => {
    setWalkInBusy(true);
    setWalkInError(null);
    try {
      const walkIn = unwrap(await nurseryService.getWalkInCustomer(), t('walkInFailed'));
      setCustomers((prev) => (prev.some((c) => c.id === walkIn.id) ? prev : [...prev, walkIn]));
      setCustomerId(walkIn.id);
      makeCounterSale();
    } catch (e) {
      setWalkInError(errorMessage(e, t('walkInFailed')));
    } finally {
      setWalkInBusy(false);
    }
  }, [makeCounterSale, t]);

  // The header saves itself once the order exists, a moment after it stops changing.
  const saveHeader = useCallback(() => enqueue(async () => {
    const id = orderIdRef.current;
    if (!id) return;
    const now = headerFields(headerRef.current);
    const patch = Object.fromEntries(
      Object.entries(now).filter(([k, v]) => sentHeaderRef.current[k] !== v),
    );
    if (!Object.keys(patch).length) return;
    try {
      unwrap(await nurseryService.updateOrder(id, patch as Partial<NurseryOrder>), t('notSaved'));
      sentHeaderRef.current = now;
      setHeaderError(null);
    } catch (e) {
      setHeaderError(t('headerNotSaved', { reason: errorMessage(e, '') }).trim());
    }
  }), [enqueue, t]);

  const orderId = order?.id ?? null;
  const editable = order ? order.editable : true;
  useEffect(() => {
    if (!orderId || !editable) return;
    const timer = setTimeout(saveHeader, 700);
    return () => clearTimeout(timer);
  }, [orderId, editable, header, saveHeader]);

  // ── Leaving ──

  const finish = async (confirm: boolean, another = false) => {
    if (!orderIdRef.current) return;
    setLeaving(true);
    setActionError(null);
    // Everything typed is written first, the header included.
    if (editable) await saveHeader();
    await chain.current;
    try {
      if (confirm) unwrap(await nurseryService.updateOrder(orderIdRef.current, { status: 'confirmed' }), t('confirmFailed'));
      if (another && order) onStartAnother({ id: order.id, order_number: order.order_number });
      else navigate(`/nursery/orders/${orderIdRef.current}`);
    } catch (e) {
      setActionError(errorMessage(e, t('confirmFailed')));
      setLeaving(false);
    }
  };

  const discard = async () => {
    if (!orderIdRef.current) return;
    setLeaving(true);
    setActionError(null);
    await chain.current;
    try {
      unwrap(await nurseryService.updateOrder(orderIdRef.current, { status: 'cancelled' }), t('discardFailed'));
      navigate('/nursery/orders');
    } catch (e) {
      setActionError(errorMessage(e, t('discardFailed')));
      setLeaving(false);
    }
  };

  // ── What is on screen ──

  const treeTypes = useMemo(() => sheetTreeTypes(rows), [rows]);
  // Filtered on what is saved, not on what is being typed: clearing a box
  // under "On this order" must not pull the row out from under the cursor.
  const crops = useMemo(() => sheetCrops(rows), [rows]);
  // "Stock at" reloads the rows; a crop no longer on the sheet filters nothing.
  const cropShown = crop != null && crops.includes(crop) ? crop : null;
  const sections = useMemo(
    () => groupSheet(filterSheet(rows, { show, search, treeTypeId, crop: cropShown, productsOnly }, saved)),
    [rows, show, search, treeTypeId, cropShown, productsOnly, saved],
  );
  const memberCount = useMemo(() => rows.filter(isProductMember).length, [rows]);
  const toggleProductsOnly = (on: boolean) => {
    setProductsOnly(on);
    try { localStorage.setItem(PRODUCTS_ONLY_KEY, on ? '1' : '0'); } catch { /* per-browser preference only */ }
  };
  const counts = useMemo(() => sheetCounts(saved), [saved]);
  const failed = Object.values(cells).filter((c) => c.state === 'error').length;

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-neutral-100 rounded-lg" />
          <div className="h-64 bg-neutral-100 rounded-lg" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{loadError}</div>
        <Button variant="outline" onClick={() => navigate('/nursery/orders')}>
          <ArrowLeft className="h-4 w-4" /> {t('back')}
        </Button>
      </div>
    );
  }

  const customerIdOrNull = customerId && !Number.isNaN(customerId) ? customerId : null;
  const gridDisabled = !customerIdOrNull || !editable || leaving;
  const walkIn = !!customers.find((c) => c.id === customerIdOrNull)?.is_walk_in;
  const unpricedCount = Object.values(unpriced).filter(Boolean).length;

  return (
    <>
      <PageHeader
        title={order ? t('title', { number: order.order_number }) : t('newOrderTitle')}
        subtitle={order ? t('subtitleOrder') : t('subtitleNew')}
        icon={<ShoppingCart className="h-6 w-6 text-white" />}
      />
      <div className="max-w-screen-2xl mx-auto px-4 pt-6 pb-40 lg:grid lg:grid-cols-[22rem_minmax(0,1fr)] lg:gap-6 lg:items-start space-y-6 lg:space-y-0">
        {/* Order details */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-5 min-w-0">
          <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('orderDetails')}</h3>

          <div>
            <label htmlFor="order-customer" className="block text-sm font-medium text-neutral-700 mb-1">
              {t('customer')} <span className="text-red-500">*</span>
            </label>
            <CustomerPicker
              id="order-customer"
              customers={customers}
              value={customerIdOrNull}
              onChange={setCustomerId}
              locked={!!order}
            />
            {walkIn ? (
              <p className="text-xs text-neutral-500 mt-1 break-words">
                {t('walkInNote')}
              </p>
            ) : !order && (
              <button
                type="button"
                onClick={chooseWalkIn}
                disabled={walkInBusy}
                className="mt-1.5 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-900 disabled:opacity-50"
              >
                <Store className="h-4 w-4" /> {t('walkIn')}
              </button>
            )}
            {walkInError && <p className="text-xs text-red-600 mt-1 break-words" role="alert">{walkInError}</p>}
            {/* Their orders already open: add to one, or draw one down. Gone once this order exists. */}
            {customerIdOrNull && !order && !walkIn && (
              <div className="mt-3">
                <CustomerOpenOrders customerId={customerIdOrNull} onAddTo={onOpenOrder} />
              </div>
            )}
            {customers.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                <Trans t={t} i18nKey="noCustomers" components={{ link: <Link to="/accounts/customers/new" className="underline" /> }} />
              </p>
            )}
            {order && order.editable && (
              <p className="text-xs text-neutral-500 mt-1 break-words">
                {t('pricesFixed')}
              </p>
            )}
            {account && !account.pays_for_itself && (
              <p className="text-xs text-neutral-500 mt-1 break-words">{t('paidBy', { payer: account.payer_name })}</p>
            )}
          </div>

          {credit && (credit.over_limit || num(credit.overdue_30) > 0) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
              {credit.over_limit && (
                <p>
                  <Trans
                    t={t}
                    i18nKey="overLimit"
                    values={{ payer: credit.payer_name, balance: money(credit.balance), limit: money(credit.credit_limit) }}
                    components={{ b: <span className="font-semibold" /> }}
                  />
                </p>
              )}
              {num(credit.overdue_30) > 0 && (
                <p>
                  {credit.over_limit
                    ? t('overdueAlso', { amount: money(credit.overdue_30) })
                    : (
                      <Trans
                        t={t}
                        i18nKey="overdue"
                        values={{ payer: credit.payer_name, amount: money(credit.overdue_30) }}
                        components={{ b: <span className="font-semibold" /> }}
                      />
                    )}
                </p>
              )}
              <p className="text-xs mt-1">{t('warningOnly')}</p>
            </div>
          )}

          <div>
            <label htmlFor="order-customer-po" className="block text-sm font-medium text-neutral-700 mb-1">
              {t('customerPo')}{poRequired && <span className="text-red-500"> *</span>}
            </label>
            <input
              id="order-customer-po"
              type="text"
              maxLength={100}
              aria-required={poRequired}
              disabled={!editable}
              value={customerPo}
              onChange={(e) => { setCustomerPo(e.target.value); setPoError(null); }}
              className={inputCls}
            />
            {poError ? (
              <p className="text-xs text-red-500 mt-1 break-words" role="alert">{poError}</p>
            ) : poRequired ? (
              <p className="text-xs text-amber-700 mt-1 break-words">{poRequiredMessage}.</p>
            ) : (
              <p className="text-xs text-neutral-500 mt-1 break-words">{t('poHint')}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-3">
            <div>
              <label htmlFor="order-date" className="block text-sm font-medium text-neutral-700 mb-1">{t('orderDate')}</label>
              <input
                id="order-date"
                type="date"
                value={orderDate}
                // Fixed once the order exists: its number was drawn for this date.
                disabled={!!order}
                onChange={(e) => setOrderDate(e.target.value)}
                className={`${inputCls} disabled:bg-neutral-50 disabled:text-neutral-600`}
              />
            </div>
            <div>
              <label htmlFor="order-requested" className="block text-sm font-medium text-neutral-700 mb-1">{t('requested')}</label>
              <input
                id="order-requested"
                type="date"
                value={requested}
                disabled={!editable}
                onChange={(e) => setRequested(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="order-estimated" className="block text-sm font-medium text-neutral-700 mb-1">{t('estimated')}</label>
              <input
                id="order-estimated"
                type="date"
                value={estimated}
                disabled={!editable}
                onChange={(e) => setEstimated(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          <fieldset disabled={!editable} className="pt-4 border-t border-neutral-100 min-w-0">
            <legend className="sr-only">{t('despatch')}</legend>
            <h4 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">{t('despatch')}</h4>
            <OrderDespatchFields
              value={despatch}
              onChange={setDespatch}
              addresses={addresses}
              zones={zones}
              timezone={timezone}
              customerId={customerIdOrNull}
              currentZoneName={currentZoneName}
              usesCrossDock={account ? account.uses_cross_dock : false}
            />
          </fieldset>

          <div>
            <label htmlFor="order-notes" className="block text-sm font-medium text-neutral-700 mb-1">{t('notes')}</label>
            <textarea
              id="order-notes"
              value={notes}
              disabled={!editable}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={t('notesPlaceholder')}
              className={`${inputCls} resize-none`}
            />
          </div>

          {headerError && (
            <p className="text-xs text-red-600 break-words" role="alert">{headerError}</p>
          )}
        </div>

        {/* The sheet */}
        <div className="space-y-4 min-w-0">
          <div className="bg-white border border-neutral-200 rounded-xl p-4 grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
            <div>
              <label htmlFor="sheet-search" className="block text-xs font-medium text-neutral-600 mb-1">{t('find')}</label>
              <div className="relative">
                <Search className="h-4 w-4 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  id="sheet-search"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('findPlaceholder')}
                  className={`${inputCls} pl-8`}
                />
              </div>
            </div>
            <div>
              <label htmlFor="sheet-crop" className="block text-xs font-medium text-neutral-600 mb-1">{t('cropType')}</label>
              <select
                id="sheet-crop"
                value={cropShown ?? ''}
                onChange={(e) => setCrop(e.target.value || null)}
                className={inputCls}
              >
                <option value="">{t('allCropTypes')}</option>
                {crops.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="sheet-size" className="block text-xs font-medium text-neutral-600 mb-1">{t('size')}</label>
              <select
                id="sheet-size"
                value={treeTypeId ?? ''}
                onChange={(e) => setTreeTypeId(e.target.value ? Number(e.target.value) : null)}
                className={inputCls}
              >
                <option value="">{t('allSizes')}</option>
                {treeTypes.map((tt) => <option key={tt.id} value={tt.id}>{tt.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="sheet-farm" className="block text-xs font-medium text-neutral-600 mb-1">{t('stockAt')}</label>
              <select
                id="sheet-farm"
                value={farmId ?? ''}
                onChange={(e) => setFarmId(e.target.value ? Number(e.target.value) : null)}
                className={inputCls}
              >
                <option value="">{t('allFarms')}</option>
                {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="sheet-show" className="block text-xs font-medium text-neutral-600 mb-1">{t('show')}</label>
              <select
                id="sheet-show"
                value={show}
                onChange={(e) => setShow(e.target.value as SheetShow)}
                className={inputCls}
              >
                <option value="all">{t('showAll')}</option>
                <option value="free">{t('showFree')}</option>
                <option value="ordered">{t('showOrdered')}</option>
              </select>
            </div>
            <div className="col-span-full">
              <label className={`flex items-start gap-2 text-sm ${customerIdOrNull ? 'text-neutral-700 cursor-pointer' : 'text-neutral-400'}`}>
                <input
                  type="checkbox"
                  checked={showPrices && !!customerIdOrNull}
                  disabled={!customerIdOrNull}
                  onChange={(e) => setShowPrices(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-neutral-300"
                />
                <span className="min-w-0 break-words">{t('showPrices')}</span>
              </label>
              {!customerIdOrNull ? (
                <p className="ml-6 text-xs text-neutral-500 break-words">{t('showPricesNeedsCustomer')}</p>
              ) : showPrices && pricing && (
                <p className="ml-6 text-xs text-neutral-500 break-words">
                  {!pricing.price_list_id ? t('pricesNoList')
                    : !pricing.in_force ? t('pricesListNotInForce', { list: pricing.price_list_name })
                      : pricing.discount_pct > 0
                        ? t('pricesFromListLess', { list: pricing.price_list_name, pct: pricing.discount_pct })
                        : t('pricesFromList', { list: pricing.price_list_name })}
                </p>
              )}
            </div>
            {memberCount > 0 && (
              <label className="col-span-full flex items-start gap-2 text-sm text-neutral-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={productsOnly}
                  onChange={(e) => toggleProductsOnly(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-neutral-300"
                />
                <span className="min-w-0 break-words">
                  {t('productsOnly', { count: memberCount })}
                </span>
              </label>
            )}
          </div>

          {previous && !order && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800 break-words" role="status">
              <Trans
                t={t}
                i18nKey="confirmedPrevious"
                values={{ number: previous.order_number }}
                components={{ link: <Link to={`/nursery/orders/${previous.id}`} className="underline font-medium" /> }}
              />
            </div>
          )}
          {order && order.editable && order.status !== 'draft' && order.status !== 'confirmed' && (
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 text-sm text-neutral-700 break-words">
              {t('allocatedNote')}
            </div>
          )}
          {releaseNote && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800 break-words" role="status">
              {releaseNote}
            </div>
          )}
          {order && !order.editable && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
              <Trans
                t={t}
                i18nKey="notEditable"
                values={{ status: t(`statusLower.${order.status}`, { defaultValue: order.status.replace(/_/g, ' ') }) }}
                components={{ link: <Link to={`/nursery/orders/${order.id}`} className="underline font-medium" /> }}
              />
            </div>
          )}
          {!customerIdOrNull && (
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 text-sm text-neutral-700">
              {t('chooseToBegin')}
            </div>
          )}
          {sheetError && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{sheetError}</div>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <p className="text-xs text-neutral-500 break-words min-w-0 flex-1 basis-60">
              {farmId ? t('freeNoteFarm') : t('freeNote')}
            </p>
            <Button
              variant="outline"
              size="sm"
              type="button"
              disabled={gridDisabled || rows.length === 0}
              title={!customerIdOrNull ? t('chooseCustomerFirst') : undefined}
              onClick={startScanning}
            >
              <ScanLine className="h-4 w-4" /> {t('scan')}
            </Button>
          </div>

          {unpricedCount > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
              {t('unpriced', { count: unpricedCount })}
            </div>
          )}

          {sections.length > 0 ? (
            <OrderSheetGrid
              sections={sections}
              quantities={quantities}
              lineTotals={lineTotals}
              unpriced={unpriced}
              cells={cells}
              disabled={gridDisabled}
              onType={onType}
              onLeave={onLeave}
              onInfo={setInfoRow}
              showPrices={showPrices && !!customerIdOrNull && !!pricing}
              discountPct={pricing?.discount_pct ?? 0}
            />
          ) : !sheetError && (
            <div className="bg-white border border-neutral-200 rounded-xl px-4 py-8 text-center text-sm text-neutral-600">
              {rows.length === 0 ? (
                <Trans t={t} i18nKey="nothingToSell" components={{ link: <Link to="/nursery/stock-items" className="underline" /> }} />
              ) : show === 'ordered' ? t('nothingOnOrder') : t('nothingMatches')}
            </div>
          )}
        </div>
      </div>

      {scanning && !gridDisabled && (
        <OrderScanDialog
          results={scans}
          cells={cells}
          noBarcodes={!rows.some((r) => r.barcode || r.retailer_barcodes?.length)}
          startManual={!coarsePointer}
          onScan={onScan}
          onTakeOne={takeOneOff}
          onClose={closeScanner}
        />
      )}

      {infoRow && (
        <CultivarInfoDialog
          subject={rowSubject(infoRow)}
          title={[infoRow.name, infoRow.rootstock_name && t('onRootstock', { name: infoRow.rootstock_name })].filter(Boolean).join(' ')}
          onClose={closeInfo}
        />
      )}

      {/* Totals and the way out */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-neutral-200 shadow-lg pb-[var(--safe-bottom)]">
        <div className="max-w-screen-2xl mx-auto px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="min-w-0 flex-1 basis-56 text-sm">
            <p className="font-semibold text-neutral-900 break-words">
              {order ? `${order.order_number} · ${t(`statusText.${order.status}`, { defaultValue: order.status.replace(/_/g, ' ') })}` : t('notStarted')}
              <span className="font-normal text-neutral-600">
                {' · '}{t('lines', { count: counts.lines })} · {t('trees', { count: counts.trees })}
                {order ? ` · ${t('totalExGst', { amount: money(order.total_value) })}` : ''}
              </span>
            </p>
            <p
              className={`text-xs break-words ${failed || actionError ? 'text-red-600' : 'text-neutral-500'}`}
              role="status"
            >
              {actionError
                ?? (failed ? t('failed', { count: failed })
                  : busy ? t('saving')
                    : order ? t('allSaved')
                      : t('createdOnFirst'))}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!order && (
              <Button variant="outline" size="sm" type="button" onClick={() => navigate('/nursery/orders')}>
                <ArrowLeft className="h-4 w-4" /> {t('cancel')}
              </Button>
            )}
            {order?.status === 'draft' && (confirmDiscard ? (
              <>
                <span className="text-sm text-neutral-700">{t('discardAsk')}</span>
                <Button variant="danger" size="sm" type="button" disabled={leaving} onClick={discard}>{t('discard')}</Button>
                <Button variant="outline" size="sm" type="button" disabled={leaving} onClick={() => setConfirmDiscard(false)}>{t('keepIt')}</Button>
              </>
            ) : (
              <Button variant="outline" size="sm" type="button" disabled={leaving} onClick={() => setConfirmDiscard(true)}>
                <Trash2 className="h-4 w-4" /> {t('discard')}
              </Button>
            ))}
            {order && !confirmDiscard && (
              <Button variant="outline" size="sm" type="button" disabled={leaving} onClick={() => finish(false)}>
                {t('openOrder')}
              </Button>
            )}
            {order?.status === 'draft' && !confirmDiscard && (
              <>
                <Button variant="outline" size="sm" type="button" disabled={leaving || counts.lines === 0} onClick={() => finish(true, true)}>
                  <Plus className="h-4 w-4" /> {t('confirmAndNew')}
                </Button>
                <Button variant="primary" size="sm" type="button" disabled={leaving || counts.lines === 0} onClick={() => finish(true)}>
                  {t('confirmOrder')} <Check className="h-4 w-4" />
                </Button>
              </>
            )}
            {order && order.status !== 'draft' && !confirmDiscard && (
              <Button variant="outline" size="sm" type="button" disabled={leaving} onClick={() => finish(false, true)}>
                <Plus className="h-4 w-4" /> {t('newOrder')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * A fresh sheet for each order. "Confirm & new order" remounts it, so nothing
 * from the last order (customer, quantities, the save queue) carries over.
 */
function OrderCreate() {
  const navigate = useNavigate();
  const [run, setRun] = useState(0);
  const [previous, setPrevious] = useState<Previous | null>(null);
  const startAnother = useCallback((done: Previous) => {
    navigate('/nursery/orders/new', { replace: true });
    setPrevious(done);
    setRun((r) => r + 1);
  }, [navigate]);
  const openOrder = useCallback((orderId: number) => {
    navigate(`/nursery/orders/new?order=${orderId}`, { replace: true });
    setPrevious(null);
    setRun((r) => r + 1);
  }, [navigate]);
  return <OrderSheet key={run} previous={previous} onStartAnother={startAnother} onOpenOrder={openOrder} />;
}

export default OrderCreate;
